import { useEffect, useRef, useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { SEO } from "@/components/seo/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { ROLE_META } from "@/lib/team-roles";

const Profile = () => {
  const { workspaces, activeWorkspace, setActiveWorkspaceId, loading: workspacesLoading } = useWorkspace();
  const fileRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const resolveAvatar = async (path: string | null) => {
    if (!path) return setAvatarPreview(null);
    const { data } = await supabase.storage.from("avatars").createSignedUrl(path, 60 * 60);
    setAvatarPreview(data?.signedUrl ?? null);
  };

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      setUserId(user.id);
      setEmail(user.email ?? "");

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, job_title, avatar_url, company_name")
        .eq("id", user.id)
        .maybeSingle();

      setFullName(profile?.full_name ?? (user.user_metadata?.full_name as string) ?? "");
      setJobTitle(profile?.job_title ?? "");
      setCompanyName(profile?.company_name ?? "");
      setAvatarUrl(profile?.avatar_url ?? null);
      await resolveAvatar(profile?.avatar_url ?? null);
      setLoading(false);
    };
    load();
  }, []);

  const handleAvatar = async (file: File) => {
    if (!userId) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${userId}/avatar-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (error) throw error;

      const { error: profileError } = await supabase
        .from("profiles")
        .update({ avatar_url: path })
        .eq("id", userId);
      if (profileError) throw profileError;

      setAvatarUrl(path);
      await resolveAvatar(path);
      toast.success("Photo updated");
    } catch (err: any) {
      toast.error(err.message || "Could not upload that photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName.trim() || null, job_title: jobTitle.trim() || null })
      .eq("id", userId);
    setSaving(false);
    if (error) {
      toast.error("Could not save your profile");
      return;
    }
    toast.success("Profile saved");
  };

  const initials = (fullName || email || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  return (
    <MainLayout>
      <SEO
        title="Your profile | Veylo AI"
        description="Manage your name, photo, job title and the workspaces you belong to."
        path="/profile"
      />
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Your profile</h1>
          <p className="text-muted-foreground">Your account details and the teams you belong to.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>This is how you appear to your teammates.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    {avatarPreview && <AvatarImage src={avatarPreview} alt={fullName || email} />}
                    <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleAvatar(file);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      {avatarUrl ? "Change photo" : "Upload photo"}
                    </Button>
                    <p className="mt-2 text-xs text-muted-foreground">JPG or PNG, up to 5 MB.</p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full name</Label>
                    <Input
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="jobTitle">Job title</Label>
                    <Input
                      id="jobTitle"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="e.g. Operations Lead"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" value={email} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" value={companyName} disabled placeholder="Set in Settings" />
                  </div>
                </div>

                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save changes
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Workspaces</CardTitle>
            <CardDescription>
              Teams you belong to and your access level in each. Select one to make it active.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {workspacesLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading workspaces…
              </div>
            ) : workspaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                You are not part of any team yet. Create one from Team Management.
              </p>
            ) : (
              workspaces.map((w) => (
                <button
                  key={w.id}
                  onClick={() => setActiveWorkspaceId(w.id)}
                  className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                    activeWorkspace?.id === w.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback>{w.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{w.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {ROLE_META[w.role].description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={ROLE_META[w.role].badgeClass}>
                      {ROLE_META[w.role].label}
                    </Badge>
                    {activeWorkspace?.id === w.id && <Check className="h-4 w-4 text-primary" />}
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Profile;
